import { useMemo, type CSSProperties } from 'react';
import { useResolvedTheme } from '@/theme/theme';
import { coverSize } from './covers';
import { rasterArt } from './raster';
import { coverUri, symbolLayers, type CoverId, type SymbolName, type ArtTheme } from './symbols';
import type { RasterArtKeys } from './raster.gen';

/** raster cover key: games without a raster cover yet (the skill games) simply find none and keep the vector */
const rasterKey = (id: CoverId) => id as RasterArtKeys['covers'];


/**
 * Game cover art: the raster cover (`covers/<game>`, one for both themes; @2x only when the tile needs it) once
 * `pnpm art:process` has produced it, else the production vector that follows the theme. Any game incl.
 * crash/pot/roulette and 'crash-hero'. Decorative.
 */
export function GameCover({
  game,
  width,
  height,
  className,
  style,
  theme,
  lazy,
}: {
  game: CoverId;
  width: number;
  height: number;
  className?: string;
  style?: CSSProperties;
  theme?: ArtTheme;
  /** below the fold: `loading="lazy"` + async decoding (the box keeps its size from `width` / `height`) */
  lazy?: boolean;
}) {
  const current = useResolvedTheme();
  const t = theme ?? current;
  const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
  const native = coverSize(game).w;
  const raster = rasterArt('covers', rasterKey(game), width * dpr > native ? 2 : 1);
  // the browser picks by its own density: the 1280 file only where the tile needs more than the 640 one
  const srcSet = raster
    ? `${rasterArt('covers', rasterKey(game), width > native ? 2 : 1)} 1x, ${rasterArt('covers', rasterKey(game), width * 2 > native ? 2 : 1)} 2x`
    : undefined;
  const src = useMemo(() => raster ?? coverUri(game, width, height, t), [raster, game, width, height, t]);
  return (
    <img
      src={src}
      srcSet={srcSet}
      width={width}
      height={height}
      loading={lazy ? 'lazy' : undefined}
      decoding={lazy ? 'async' : undefined}
      data-raster={raster ? '' : undefined}
      alt=""
      aria-hidden="true"
      draggable={false}
      className={className}
      style={{ display: 'block', objectFit: 'cover', objectPosition: 'right center', ...style }}
    />
  );
}

/** One slot symbol (100×100 art), scaled to `size`. */
export function SlotSymbol({
  name,
  size,
  className,
  style,
  theme,
}: {
  name: SymbolName;
  size?: number | string;
  className?: string;
  style?: CSSProperties;
  theme?: ArtTheme;
}) {
  const current = useResolvedTheme();
  const t = theme ?? current;
  const { under, uri: src } = useMemo(() => symbolLayers(name, t), [name, t]);
  return (
    <img
      src={src}
      alt=""
      aria-hidden="true"
      draggable={false}
      className={className}
      style={{
        width: size ?? '70%',
        height: size ?? '70%',
        display: 'block',
        // a raster disc under a carved sign (Koi Pond ranks): same 100×100 box
        ...(under ? { background: `url("${under}") center / contain no-repeat` } : null),
        ...style,
      }}
    />
  );
}
